import { LINK_TYPES, type Issue, type LinkType } from "@gjwtrack/domain";
import { useEffect, useState } from "react";
import { client, useClient } from "../lib/client.ts";
import { issueKey } from "../lib/format.ts";
import { Button, Dialog, DialogContent, DialogHeader, Input, InputSize, Panel, Select, SelectType, type SelectItem } from "../ui/ring.ts";

/** Link this issue to another: pick a type, find the target with the query language. Ring Dialog handles Esc, overlay and focus. */
export function LinkDialog({ issueId, onClose }: { issueId: number; onClose: () => void }) {
  const schema = useClient((s) => s.schema);
  const [choice, setChoice] = useState<{ type: LinkType; outward: boolean }>({ type: "relates", outward: true });
  const [q, setQ] = useState("");
  const [results, setResults] = useState<Issue[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const timer = setTimeout(() => {
      client.http
        .search(q, 20)
        .then((page) => !cancelled && setResults(page.issues.filter((i) => i.id !== issueId)))
        .catch((e) => !cancelled && setError((e as Error).message));
    }, 200);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [q, issueId]);

  const link = async (target: Issue): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      const [from, to] = choice.outward || LINK_TYPES[choice.type].symmetric ? [issueId, target.id] : [target.id, issueId];
      await client.linkIssues(from, to, choice.type);
      if (from !== issueId) await client.loadLinks(issueId);
      onClose();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const types: SelectItem[] = (Object.keys(LINK_TYPES) as LinkType[]).flatMap((t) => {
    const def = LINK_TYPES[t];
    return def.symmetric ? [{ key: `${t}:out`, label: def.outward }] : [{ key: `${t}:out`, label: def.outward }, { key: `${t}:in`, label: def.inward }];
  });
  const current = types.find((i) => i.key === `${choice.type}:${choice.outward ? "out" : "in"}`) ?? null;

  if (!schema) return null;
  return (
    <Dialog show label="Link issue" onCloseAttempt={onClose} showCloseButton trapFocus autoFocusFirst={false}>
      <DialogHeader>Link issue</DialogHeader>
      <DialogContent>
        <div className="flex items-center gap-2">
          <Select
            type={SelectType.BUTTON}
            data={types}
            selected={current}
            onChange={(item: SelectItem | null) => {
              if (!item) return;
              const [t, d] = String(item.key).split(":") as [LinkType, string];
              setChoice({ type: t, outward: d === "out" });
            }}
          />
          <Input size={InputSize.FULL} autoFocus placeholder="Search issues (query language)" inputClassName="font-mono" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        {error && <div className="mt-2 text-danger">{error}</div>}
        <ul className="mt-2 max-h-80 overflow-y-auto">
          {results.map((i) => (
            <li key={i.id}>
              <button className="flex w-full items-center gap-3 rounded px-2 py-1.5 text-left hover:bg-sunken" disabled={busy} onClick={() => void link(i)}>
                <span className={`w-24 shrink-0 font-mono text-xs ${i.resolvedAt !== null ? "text-faint line-through" : "text-muted"}`}>{issueKey(schema, i)}</span>
                <span className="truncate">{i.summary}</span>
              </button>
            </li>
          ))}
          {results.length === 0 && <li className="px-2 py-2 text-muted">No matches.</li>}
        </ul>
      </DialogContent>
      <Panel>
        <Button onClick={onClose}>Cancel</Button>
      </Panel>
    </Dialog>
  );
}
